import { defineComponent, h, defineAsyncComponent, nextTick } from 'vue';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import TranslationContainer from '../TranslationContainer.jsx';
import Pg2Row from './Pg2Row.jsx';
import AceEditor from '../3rdParty/AceEditor.jsx';

const PropertyGrid2 = defineAsyncComponent(() => import('../form/PropertyGrid2.jsx'));

export default defineComponent({
    name: 'form2-referenced-document-schema',
    components: {
        PropertyGrid2,
        TranslationContainer,
        Pg2Row,
        AceEditor,
    },
    data: function () {
        return {
            condition_expn: null,
            control_schema: null,
            asjson: false,
            uniquekey: null,
            rawJsonEditorHeight: '100px',
        };
    },
    created() {
        this.uniquekey = utils.generateUUID();
        switch (this.schema.formatData.DocumentType) {
            case 'UserControl':
                this.watcher$ = this.$watch('cmodel.ControlURL', function (val, oldval) {
                    this.Refresh();
                });
                break;

            case 'Action':
                this.watcher$ = this.$watch('cmodel.ActionURL', function (val, oldval) {
                    this.Refresh();
                });
                break;
        }
    },
    unmounted() {
        if (this.watcher$) this.watcher$();
    },
    async mounted() {
        this.Refresh();
        nextTick(() => this.ensureRawJsonAceResizeObserver());
    },
    updated() {
        nextTick(() => this.ensureRawJsonAceResizeObserver());
    },
    beforeUnmount() {
        this.teardownRawJsonAceResizeObserver();
    },
    computed: {
        ...utils.forms.computed,
    },
    methods: {
        ...methods,
        referencedData() {
            const doctype = this.schema.formatData.DocumentType;
            if (doctype === 'UserControl') return this.cmodel.ControlData;
            if (doctype === 'Action') return this.cmodel.ActionData;
            return undefined;
        },
        ensureRawJsonAceResizeObserver() {
            if (typeof ResizeObserver === 'undefined') return;
            const host = this.$refs.referencedDocRawJsonAceHost;
            if (!host) {
                this.teardownRawJsonAceResizeObserver();
                return;
            }
            if (this._referencedDocRawJsonResizeObserved === host) return;
            this.teardownRawJsonAceResizeObserver();
            this._referencedDocRawJsonResizeObserved = host;
            this._referencedDocRawJsonResizeObserver = new ResizeObserver(() => {
                const px = Math.round(host.offsetHeight || 0);
                if (!px) return;
                const next = `${px}px`;
                if (this.rawJsonEditorHeight !== next) this.rawJsonEditorHeight = next;
            });
            this._referencedDocRawJsonResizeObserver.observe(host);
        },
        teardownRawJsonAceResizeObserver() {
            if (this._referencedDocRawJsonResizeObserver) {
                this._referencedDocRawJsonResizeObserver.disconnect();
                this._referencedDocRawJsonResizeObserver = null;
            }
            this._referencedDocRawJsonResizeObserved = null;
        },
        sync_json(text) {
            try {
                const parsed = JSON.parse(text);
                const doctype = this.schema.formatData.DocumentType;
                if (doctype === 'UserControl') this.cmodel.ControlData = parsed;
                else if (doctype === 'Action') this.cmodel.ActionData = parsed;
            } catch (e) {
                utils.warn('referencedDocumentSchema in propertygrid could not parse JSON', e);
            }
        },
        async Refresh() {
            const doctype = this.schema.formatData.DocumentType;

            switch (doctype) {
                case 'UserControl': {
                    let doc1 = await utils.api.get('Apps/UIWhole/' + doctype + '/' + this.cmodel.ControlURL);
                    if (!doc1) doc1 = await utils.api.get('Apps/UIWhole/' + doctype + '/public/' + this.cmodel.ControlURL);
                    if (doc1) this.control_schema = await utils.schema.resolve(doc1.ControlDataSchema);
                    break;
                }

                case 'Action': {
                    let doc2 = await utils.api.get('Apps/UIWhole/' + doctype + '/' + this.cmodel.ActionURL);
                    if (!doc2) doc2 = await utils.api.get('Apps/UIWhole/' + doctype + '/public/' + this.cmodel.ActionURL);
                    if (doc2) this.control_schema = await utils.schema.resolve(doc2.ActionDataSchema);
                    break;
                }

                case 'Schema': {
                    const property = this.cmodel[this.schema.formatData.Property];
                    utils.log('Property: ' + property);
                    break;
                }

                default:
                    return;
            }
        },
    },
    props: {
        name: '',
        schemakey: '',
        root: null,
        schema: null,
        cmodel: null,
        extra: null,
        readonly: false,
        child: 0,
        navigateToModel: null,
        propertygrid: false,
    },
    render() {
        if (!this.Condition) return null;

        let desc;
        if (this.schema.description)
            desc = (
                <i
                    class="mdi mdi-information"
                    title={this.schema.description}
                    style={{ color: 'var(--v-checkboxGrey-base)', fontSize: 'small', marginLeft: '3px' }}
                ></i>
            );

        const doctype = this.schema.formatData.DocumentType;
        const choice = doctype === 'UserControl' ? this.cmodel.ControlURL : this.cmodel.ActionURL;

        const name = <span style={{ marginLeft: this.child * 15 + 'px' }}>{this.name}</span>;

        const showJsonToggle = doctype === 'UserControl' || doctype === 'Action';
        let jsontgl = null;
        if (showJsonToggle) {
            const jsonico = this.asjson ? (
                <i class="mdi mdi-eye-off" title="JSON view"></i>
            ) : (
                <i class="mdi mdi-eye" title="JSON view"></i>
            );
            jsontgl = (
                <span style={{ fontSize: 'smaller', cursor: 'pointer', color: 'gray' }} onClick={() => (this.asjson = !this.asjson)}>
                    {jsonico}
                </span>
            );
        }

        const data = this.referencedData();
        if (showJsonToggle && (this.asjson || typeof data === 'string')) {
            const rawjson = JSON.stringify(data, null, 4);
            return (
                <Pg2Row>
                    {{
                        label: () => (
                            <span class="c-PropertyGrid2__labelInner">
                                {name} {jsontgl} {desc}
                            </span>
                        ),
                        default: () => (
                            <div
                                ref="referencedDocRawJsonAceHost"
                                class="c-PropertyGrid2__editorHost"
                                style={{
                                    resize: 'vertical',
                                    width: '100%',
                                    height: this.rawJsonEditorHeight,
                                    padding: '5px',
                                    overflow: 'auto',
                                    position: 'relative',
                                    minWidth: 0,
                                }}
                            >
                                <AceEditor
                                    editorId={'Editor_' + this.uniquekey}
                                    lang="json"
                                    onChangeContent={(text) => this.sync_json(text)}
                                    content={rawjson}
                                ></AceEditor>
                            </div>
                        ),
                    }}
                </Pg2Row>
            );
        }

        let form;
        if (this.control_schema) {
            form = (
                <PropertyGrid2
                    name={choice}
                    root={this.root}
                    schema={this.control_schema}
                    form={['*']}
                    cmodel={doctype === 'UserControl' ? this.cmodel.ControlData : this.cmodel.ActionData}
                    child={0}
                    navigateToModel={this.root.navigateToModel || this.navigateToModel}
                >
                </PropertyGrid2>
            );
        } else form = <TranslationContainer context={this} value={'Loading...'} />;

        return (
            <Pg2Row>
                {{
                    label: () => (
                        <span class="c-PropertyGrid2__labelInner">
                            {name} {jsontgl} {desc}
                        </span>
                    ),
                    default: () => form,
                }}
            </Pg2Row>
        );
    },
});
